import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { INBOUND_CALLS_LIMITS } from '../../lib/speedToLead/inboundCalls';
import { CALLS_LIMITS } from '../../lib/speedToLead/callSettings';
import { SETTINGS_LIMITS } from '../../lib/speedToLead/settings';
import { buildStaffRows, toStaffList } from '../../lib/speedToLead/staffView';
import { serviceVisibility } from '../../lib/kyckConnect/setupStatus';
import { noAnswerSentence } from '../../lib/speedToLead/noAnswerCopy';
import type { NoAnswerAction, SpeedToLeadSettings, StaffRow } from '../../types/speedToLead';
import { fetchClubSettings, saveClubSettings, type SettingsBody } from './api';
import { createBotConfig } from '../leadBot/api';
import { defaultNewBotArgs } from '../leadBot/newBot';
import { StaffListEditor } from '../../components/speedToLead/StaffListEditor';
import { MessagesEditor, OutboundWhisperEditor } from '../../components/speedToLead/MessagesEditor';

// ONE CLUB'S SPEED TO LEAD SETTINGS (extracted from the old Settings -> Speed to Lead page, KyckConnect redesign Phase 1). It
// takes the accountId and renders NO club picker. ⚠️ PHASE 2 SPLIT IT INTO THREE SECTIONS, one per setup step, over the SAME
// settings document and the SAME save body: `services` (the AI SMS and calls switches, and which bot answers), `calls` (the Speed
// to Lead step: limits, recording, the staff list) and `callbacks` (the Number and call-backs step: inbound forwarding). Each save
// sends the whole body, so a section never resets a field it does not show. Which fields appear follows `serviceVisibility`, read
// from the SAVED settings (never the unsaved ticks), so a field never appears or vanishes mid-edit.
//
// EVERYTHING DEFAULTS OFF and nothing here switches a club on by itself: a club with no saved settings shows the defaults
// (AI SMS off, calls off), and an admin ticks and saves, one club at a time. THE SPEED TO LEAD (CALLS) TOGGLE IS LIVE, PER CLUB,
// ADMIN ONLY, DEFAULT OFF. A call is only placed when the club's switch AND the system-wide kill switch
// (SPEED_TO_LEAD_CALLS_ENABLED) are both on; while the kill switch is off the form says so and the toggle still saves.
// Writes are admin-only; anyone signed in can read. The club's call log and the test-lead panel live on their own tabs/steps.

function errMessage(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong.';
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {description && <p className="mb-4 mt-0.5 text-sm text-slate-500">{description}</p>}
      {children}
    </section>
  );
}

function NumberField({ label, value, onChange, min, max, disabled, suffix }: {
  label: string; value: string; onChange: (v: string) => void; min: number; max: number; disabled: boolean; suffix?: string;
}) {
  return (
    <label className="block text-sm text-slate-700">
      <span className="mb-1 block">{label}</span>
      <div className="flex items-center gap-2">
        <Input type="number" inputMode="numeric" min={min} max={max} value={value} disabled={disabled} className="w-28" onChange={(e) => onChange(e.target.value)} />
        {suffix && <span className="text-slate-500">{suffix}</span>}
      </div>
      <span className="text-xs text-slate-400">{min} to {max}</span>
    </label>
  );
}

type FormState = {
    samEnabled: boolean; botConfigId: string; respectStaffedHours: boolean; fallbackMinutes: string; qualityCallSeconds: string;
    noAnswerAction: NoAnswerAction; retryAfterMinutes: string; staffRingSeconds: string; roundTwoDelaySeconds: string; maxRounds: string;
    // Inbound call forwarding (Stage 2.5). Off by default.
    inEnabled: boolean; inPrimary: string; inThenStaff: boolean; inRing: string; inStaffedHours: boolean;
    inWhisper: string; inMissed: string; inLocked: boolean;
    // Outbound calls + recording (Stage 3 / 3B / 3C). Calls are OFF by default.
    callsOn: boolean; callAtNextOpening: boolean; maxConcurrent: string; maxPerDay: string;
    recordCalls: boolean; transcribeCalls: boolean; retentionDays: string; outWhisper: string;
  };

// The form's starting state from the stored settings. A function (not an effect) so a render with the data already cached has the form at once.
function formFrom(s: SpeedToLeadSettings): FormState {
  return {
    samEnabled: s.samEnabled, botConfigId: s.botConfigId ?? '', respectStaffedHours: s.respectStaffedHours,
    fallbackMinutes: String(s.fallbackMinutes), qualityCallSeconds: String(s.qualityCallSeconds), noAnswerAction: s.noAnswerAction,
    retryAfterMinutes: s.retryAfterMinutes === null ? '' : String(s.retryAfterMinutes), staffRingSeconds: String(s.staffRingSeconds),
    roundTwoDelaySeconds: String(s.roundTwoDelaySeconds), maxRounds: String(s.maxRounds),
    inEnabled: s.inboundCalls.enabled, inPrimary: s.inboundCalls.primaryNumber ?? '', inThenStaff: s.inboundCalls.thenStaffList,
    inRing: String(s.inboundCalls.ringSeconds), inStaffedHours: s.inboundCalls.respectStaffedHours,
    inWhisper: s.inboundCalls.whisperMessage ?? '', inMissed: s.inboundCalls.missedCallMessage ?? '', inLocked: s.inboundCalls.messagesLockedByKyck,
    callsOn: s.speedToLeadEnabled, callAtNextOpening: s.calls.callAtNextOpening, maxConcurrent: String(s.calls.maxConcurrentCalls),
    maxPerDay: String(s.calls.maxCallsPerDay), recordCalls: s.calls.recordCalls, transcribeCalls: s.calls.transcribeCalls,
    retentionDays: String(s.calls.recordingRetentionDays), outWhisper: s.calls.whisperMessage ?? '',
  };
}

export type ClubSettingsSection = 'services' | 'calls' | 'callbacks';

export function ClubSettingsForm({ accountId, isAdmin, section = 'calls', relay, accountName, accountIndustry = null }: { accountId: string; isAdmin: boolean; section?: ClubSettingsSection; relay?: React.ReactNode; accountName?: string; accountIndustry?: string | null }) {
  const qc = useQueryClient();
  // "Create bot for <club>": the Services step used to refuse the AI SMS switch with "no bot" while the bot could only be made inside the
  // step that switch gates. A new bot starts in Setting up, so creating one here sends nothing to anyone.
  const createBot = useMutation({
    mutationFn: () => createBotConfig(defaultNewBotArgs({ accountId, accountName: accountName ?? 'Club', industry: accountIndustry })),
    onSuccess: () => {
      toast.success('Bot created. It starts in Setting up, so nothing is sent to real leads yet.');
      void qc.invalidateQueries({ queryKey: ['speed-to-lead', 'club', accountId] });
      void qc.invalidateQueries({ queryKey: ['kyckconnect'] });
      void qc.invalidateQueries({ queryKey: ['lead-bot'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const query = useQuery({ queryKey: ['speed-to-lead', 'club', accountId], queryFn: () => fetchClubSettings(accountId), enabled: accountId !== '' });

  // Form state is strings for numbers, so an emptied box stays empty while retyped.
  const [form, setForm] = useState<FormState | null>(() => (query.data ? formFrom(query.data.settings) : null));
  const [rows, setRows] = useState<StaffRow[]>(() => (query.data ? buildStaffRows(query.data.team, query.data.settings.staffList) : []));

  useEffect(() => {
    const s = query.data?.settings;
    if (!s || !query.data) return;
    setForm(formFrom(s));
    setRows(buildStaffRows(query.data.team, s.staffList));
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('Nothing to save.');
      const body: SettingsBody = {
        samEnabled: form.samEnabled,
        speedToLeadEnabled: form.callsOn,
        calls: {
          callAtNextOpening: form.callAtNextOpening,
          maxConcurrentCalls: Number(form.maxConcurrent),
          maxCallsPerDay: Number(form.maxPerDay),
          recordCalls: form.recordCalls,
          transcribeCalls: form.recordCalls && form.transcribeCalls,
          recordingRetentionDays: Number(form.retentionDays),
          whisperMessage: form.outWhisper.trim() === '' ? null : form.outWhisper,
        },
        botConfigId: form.botConfigId === '' ? null : form.botConfigId,
        respectStaffedHours: form.respectStaffedHours,
        fallbackMinutes: Number(form.fallbackMinutes),
        qualityCallSeconds: Number(form.qualityCallSeconds),
        noAnswerAction: form.noAnswerAction,
        retryAfterMinutes: form.retryAfterMinutes.trim() === '' ? null : Number(form.retryAfterMinutes),
        staffRingSeconds: Number(form.staffRingSeconds),
        roundTwoDelaySeconds: Number(form.roundTwoDelaySeconds),
        maxRounds: Number(form.maxRounds),
        staffList: toStaffList(rows),
        inboundCalls: {
          enabled: form.inEnabled,
          primaryNumber: form.inPrimary.trim() === '' ? null : form.inPrimary.trim(),
          thenStaffList: form.inThenStaff,
          ringSeconds: Number(form.inRing),
          respectStaffedHours: form.inStaffedHours,
          whisperMessage: form.inWhisper.trim() === '' ? null : form.inWhisper,
          missedCallMessage: form.inMissed.trim() === '' ? null : form.inMissed,
          messagesLockedByKyck: form.inLocked,
        },
      };
      return saveClubSettings(accountId, body);
    },
    onSuccess: () => {
      toast.success(section === 'services' ? 'Services saved.' : 'Speed to Lead settings saved.');
      void qc.invalidateQueries({ queryKey: ['speed-to-lead', 'club', accountId] });
      // The checklist and the Overview read the same settings: refresh them so steps, statuses and hidden lines follow.
      void qc.invalidateQueries({ queryKey: ['kyckconnect'] });
    },
    onError: (err) => toast.error(errMessage(err)),
  });

  const set = <K extends keyof NonNullable<typeof form>>(key: K, value: NonNullable<typeof form>[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));
  const L = SETTINGS_LIMITS;
  const readOnly = !isAdmin;

  // Which fields show follows the SAVED settings, never the unsaved ticks.
  const saved = query.data?.settings;
  const vis = serviceVisibility({ samEnabled: saved?.samEnabled ?? false, speedToLeadEnabled: saved?.speedToLeadEnabled ?? false, inboundEnabled: saved?.inboundCalls.enabled ?? false });
  const saveButton = (label: string) =>
    isAdmin ? (
      <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : label}</Button>
    ) : (
      <p className="text-xs text-slate-500">Only an admin can change these settings.</p>
    );

  return (
    <div data-testid="club-settings-form" data-section={section}>
      {query.isLoading && <Spinner />}
      {query.error && <p className="mt-3 text-sm text-red-600">{errMessage(query.error)}</p>}
      {query.data && form && section === 'services' && (
        <div className="mt-2 space-y-4" data-testid="services-step">
          {query.data.isDefault && <p className="rounded bg-slate-50 p-3 text-sm text-slate-600">No settings saved for this club yet. Showing the defaults (everything off).</p>}
          <div className="rounded-md border border-slate-200 p-4" data-testid="service-card-relay">
            <h3 className="text-sm font-semibold text-slate-900">Lead relay</h3>
            {relay}
          </div>

          <div className="space-y-3 rounded-md border border-slate-200 p-4" data-testid="service-card-ai-sms">
            <h3 className="text-sm font-semibold text-slate-900">AI SMS</h3>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <input type="checkbox" className="h-4 w-4" checked={form.samEnabled} disabled={readOnly || (query.data.bots.length === 0 && !form.samEnabled)} onChange={(e) => set('samEnabled', e.target.checked)} />
              AI SMS answers this club&apos;s leads by text
            </label>
            {query.data.bots.length === 0 && (
              <div className="ml-6 space-y-1 rounded bg-amber-50 p-3 text-sm text-amber-900" data-testid="no-bot">
                <p>This club has no bot yet, so AI SMS cannot be switched on. Create one first.</p>
                <Button size="sm" onClick={() => createBot.mutate()} disabled={createBot.isPending}>{createBot.isPending ? 'Creating…' : `Create bot for ${accountName ?? 'this club'}`}</Button>
              </div>
            )}
            <p className="ml-6 text-xs text-slate-500">
              Whether the assistant texts on its own is still decided by the bot&apos;s mode and test-phone allowlist. A lead that is not allowlisted gets a draft that waits for approval.
            </p>
            <label className="block max-w-md text-sm text-slate-700">
              <span className="mb-1 block">Which bot answers</span>
              <select className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm" value={form.botConfigId} disabled={readOnly} onChange={(e) => set('botConfigId', e.target.value)} aria-label="Bot">
                <option value="">The club&apos;s active bot</option>
                {query.data.bots.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}{b.isActive ? '' : ' (off)'}{b.numberWarning ? ' (number not in a pool)' : ''}</option>
                ))}
              </select>
              {query.data.bots.filter((b) => b.numberWarning && (form.botConfigId === '' ? b.isActive : b.id === form.botConfigId)).map((b) => (
                <span key={b.id} role="alert" data-testid="unpooled-warning" className="mt-1 block text-xs text-amber-700">{b.name}: {b.numberWarning}</span>
              ))}
            </label>
          </div>

          <div className="space-y-3 rounded-md border border-slate-200 p-4" data-testid="service-card-calls">
            <h3 className="text-sm font-semibold text-slate-900">Speed to Lead calls</h3>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <input type="checkbox" className="h-4 w-4" checked={form.callsOn} disabled={readOnly} aria-describedby="stl-calls-note" onChange={(e) => set('callsOn', e.target.checked)} />
              Speed to Lead rings the club&apos;s staff first, then the lead
            </label>
            <p id="stl-calls-note" className="ml-6 text-xs text-slate-500">
              Off by default. Needs at least one person on the staff call list, which you set up in the Speed to Lead step once this is on.
            </p>
            {!query.data.callsAvailable && (
              <p role="status" data-testid="calls-switched-off" className="ml-6 rounded bg-amber-50 p-2 text-xs text-amber-800">
                Calls are switched off system-wide. This switch saves, but no call is placed until calls are switched on for everyone.
              </p>
            )}
          </div>
          {saveButton('Save services')}
        </div>
      )}

      {query.data && form && section === 'calls' && (
        <div className="mt-2 space-y-6" data-testid="speed-to-lead-step">
          {query.data.isDefault && <p className="rounded bg-slate-50 p-3 text-sm text-slate-600">No settings saved for this club yet. Showing the defaults (everything off).</p>}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4" checked={form.respectStaffedHours} disabled={readOnly} onChange={(e) => set('respectStaffedHours', e.target.checked)} />
            Only ring during the club&apos;s staffed hours (read from its Business Profile)
          </label>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {vis.aiSmsFieldsInCalls && (
              <NumberField label="AI SMS waits before texting if the call has not reached the lead" suffix="minutes" value={form.fallbackMinutes} min={L.fallbackMinutes.min} max={L.fallbackMinutes.max} disabled={readOnly} onChange={(v) => set('fallbackMinutes', v)} />
            )}
            <NumberField label="Ring each person for" suffix="seconds" value={form.staffRingSeconds} min={L.staffRingSeconds.min} max={L.staffRingSeconds.max} disabled={readOnly} onChange={(v) => set('staffRingSeconds', v)} />
            <NumberField label="Wait before the second round" suffix="seconds" value={form.roundTwoDelaySeconds} min={L.roundTwoDelaySeconds.min} max={L.roundTwoDelaySeconds.max} disabled={readOnly} onChange={(v) => set('roundTwoDelaySeconds', v)} />
            <NumberField label="Rounds of the staff list" value={form.maxRounds} min={L.maxRounds.min} max={L.maxRounds.max} disabled={readOnly} onChange={(v) => set('maxRounds', v)} />
            <NumberField label="Retry later after (leave empty for no retry)" suffix="minutes" value={form.retryAfterMinutes} min={L.retryAfterMinutes.min} max={L.retryAfterMinutes.max} disabled={readOnly} onChange={(v) => set('retryAfterMinutes', v)} />
          </div>

          <div className="space-y-1 text-sm text-slate-700" data-testid="no-answer-note">
            <p className="font-medium">If the lead does not answer</p>
            <p>{noAnswerSentence(vis.aiSmsFieldsInCalls)}</p>
            <p>Voicemail and call screening: the system says nothing to the lead and never hangs up on a machine. The staff member stays on the line and decides. If the call ends without a person ever picking up, it counts as not reached.</p>
          </div>

          <div className="space-y-3 rounded-md border border-slate-200 p-4" data-testid="calls-card">
            <h3 className="text-sm font-medium text-slate-800">Calls</h3>
            <p className="text-xs text-slate-500">
              Limits and recording for this club&apos;s Speed to Lead calls, and for calls forwarded to the club. Callers are not told a call is recorded: telling people is the club&apos;s responsibility.
            </p>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4" checked={form.callAtNextOpening} disabled={readOnly} onChange={(e) => set('callAtNextOpening', e.target.checked)} />
              Out of staffed hours, call at the next opening (when it is within 18 hours)
            </label>
            <div className="grid gap-4 sm:grid-cols-3">
              <NumberField label="Calls at once" value={form.maxConcurrent} min={CALLS_LIMITS.maxConcurrentCalls.min} max={CALLS_LIMITS.maxConcurrentCalls.max} disabled={readOnly} onChange={(v) => set('maxConcurrent', v)} />
              <NumberField label="Calls per day" value={form.maxPerDay} min={CALLS_LIMITS.maxCallsPerDay.min} max={CALLS_LIMITS.maxCallsPerDay.max} disabled={readOnly} onChange={(v) => set('maxPerDay', v)} />
              <NumberField label="Keep recordings for" suffix="days" value={form.retentionDays} min={CALLS_LIMITS.recordingRetentionDays.min} max={CALLS_LIMITS.recordingRetentionDays.max} disabled={readOnly} onChange={(v) => set('retentionDays', v)} />
            </div>
            <p className="text-xs text-slate-500">{vis.aiSmsFieldsInCalls ? 'At a limit, AI SMS texts the lead straight away and Kyck is alerted.' : 'At a limit, no call is placed and Kyck is alerted.'}</p>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4" checked={form.recordCalls} disabled={readOnly} onChange={(e) => set('recordCalls', e.target.checked)} />
              Record calls
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4" checked={form.recordCalls && form.transcribeCalls} disabled={readOnly || !form.recordCalls} onChange={(e) => set('transcribeCalls', e.target.checked)} />
              Transcribe recorded calls
            </label>
            <OutboundWhisperEditor value={form.outWhisper} readOnly={readOnly} onChange={(v) => set('outWhisper', v)} />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium text-slate-800">Staff call list</h3>
            <StaffListEditor rows={rows} onChange={setRows} disabled={readOnly} />
          </div>
          {saveButton('Save settings')}
        </div>
      )}

      {query.data && form && section === 'callbacks' && (
        <div className="mt-2 space-y-4" data-testid="callbacks-section">
          <div className="space-y-3 rounded-md border border-slate-200 p-4" data-testid="inbound-calls-card">
            <h3 className="text-sm font-medium text-slate-800">Inbound call-backs</h3>
            <p className="text-xs text-slate-500">
              When a lead calls back the number AI SMS texted from, ring this club&apos;s primary number. The person who answers hears who is calling and presses a key to take the call.
              Whether the call is recorded follows the Calls settings in the Speed to Lead step. Off until you switch it on and give a number; otherwise the caller hears a short message and the club is alerted.
            </p>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <input type="checkbox" className="h-4 w-4" checked={form.inEnabled} disabled={readOnly} onChange={(e) => set('inEnabled', e.target.checked)} />
              Forward inbound calls to the club
            </label>
            <label className="block max-w-xs text-sm text-slate-700">
              <span className="mb-1 block">Primary number (reception or manager)</span>
              <Input value={form.inPrimary} disabled={readOnly} placeholder="e.g. 0412 345 678" onChange={(e) => set('inPrimary', e.target.value)} aria-label="Primary number" />
              <span className="text-xs text-slate-400">An Australian mobile or landline, never one of our own numbers.</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4" checked={form.inThenStaff} disabled={readOnly} onChange={(e) => set('inThenStaff', e.target.checked)} />
              Then ring the staff call list, one at a time
            </label>
            <NumberField label="Ring each number for" suffix="seconds" value={form.inRing} min={INBOUND_CALLS_LIMITS.ringSeconds.min} max={INBOUND_CALLS_LIMITS.ringSeconds.max} disabled={readOnly} onChange={(v) => set('inRing', v)} />
            <p className="text-xs text-muted-foreground">Most mobiles divert to voicemail at about 20–25 seconds. Whoever answers must press a key to take the call, so voicemail can&apos;t take it.</p>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4" checked={form.inStaffedHours} disabled={readOnly} onChange={(e) => set('inStaffedHours', e.target.checked)} />
              Only ring during the club&apos;s staffed hours
            </label>
            <div className="space-y-3 border-t border-slate-200 pt-3" data-testid="inbound-messages">
              <h4 className="text-sm font-medium text-slate-800">Spoken messages</h4>
              <MessagesEditor whisperMessage={form.inWhisper} missedCallMessage={form.inMissed} readOnly={readOnly} onChange={(n) => setForm({ ...form, inWhisper: n.whisperMessage, inMissed: n.missedCallMessage })} />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4" checked={form.inLocked} disabled={readOnly} onChange={(e) => set('inLocked', e.target.checked)} />
                Lock these messages (the club sees them read-only in its portal)
              </label>
            </div>
          </div>
          {saveButton('Save call-backs')}
        </div>
      )}
    </div>
  );
}
